import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import { useToast } from './ui/Toast';
import './AnnouncementManagement.css';
import {
  Megaphone, Plus, Edit, Trash2, Eye, EyeOff, X,
  Calendar, Image, Link, Target, Sparkles, Heart,
  Moon, Gift, Egg, PartyPopper, AlertCircle,
  BarChart3, MousePointer, XCircle, TrendingUp
} from 'lucide-react';

// Theme configurations with colors and icons
const THEME_CONFIG = {
  NEUTRAL: { label: 'Neutral', colors: ['#0d7463', '#059669'], icon: Megaphone, textColor: 'white' },
  VALENTINES: { label: "Valentine's", colors: ['#ec4899', '#db2777'], icon: Heart, textColor: 'white' },
  RAMADAN: { label: 'Ramadan', colors: ['#7c3aed', '#5b21b6'], icon: Moon, textColor: 'white' },
  CHRISTMAS: { label: 'Christmas', colors: ['#dc2626', '#16a34a'], icon: Gift, textColor: 'white' },
  EASTER: { label: 'Easter', colors: ['#f472b6', '#a855f7'], icon: Egg, textColor: 'white' },
  NEW_YEAR: { label: 'New Year', colors: ['#fbbf24', '#f59e0b'], icon: PartyPopper, textColor: '#1f2937' }
};

const AnnouncementManagement = () => {
  const toast = useToast();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [targetAudiences, setTargetAudiences] = useState([{ value: 'ALL', label: 'All Merchants' }]);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    imageUrl: '',
    buttonText: '',
    buttonUrl: '',
    theme: 'NEUTRAL',
    targetAudience: 'ALL',
    isActive: true,
    priority: 0,
    startDate: '',
    endDate: '',
    utmSource: '',
    utmMedium: '',
    utmCampaign: ''
  });

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/announcements?status=${statusFilter}`);
      setAnnouncements(response.announcements || []);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  // Fetch available target audiences from admin-configured tiers
  const fetchConfig = useCallback(async () => {
    try {
      const response = await api.get('/admin/announcements/themes/list');
      if (response.audiences) {
        setTargetAudiences(response.audiences);
      }
    } catch (err) {
      console.error('Error fetching announcement config:', err);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchAnnouncements();
  }, [fetchConfig, fetchAnnouncements]);

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      imageUrl: '',
      buttonText: '',
      buttonUrl: '',
      theme: 'NEUTRAL',
      targetAudience: 'ALL',
      isActive: true,
      priority: 0,
      startDate: '',
      endDate: '',
      utmSource: '',
      utmMedium: '',
      utmCampaign: ''
    });
    setEditingAnnouncement(null);
  };

  const handleOpenModal = (announcement = null) => {
    if (announcement) {
      setEditingAnnouncement(announcement);
      setFormData({
        title: announcement.title || '',
        message: announcement.message || '',
        imageUrl: announcement.imageUrl || '',
        buttonText: announcement.buttonText || '',
        buttonUrl: announcement.buttonUrl || '',
        theme: announcement.theme || 'NEUTRAL',
        targetAudience: announcement.targetAudience || 'ALL',
        isActive: announcement.isActive ?? true,
        priority: announcement.priority || 0,
        startDate: announcement.startDate ? announcement.startDate.split('T')[0] : '',
        endDate: announcement.endDate ? announcement.endDate.split('T')[0] : '',
        utmSource: announcement.utmSource || '',
        utmMedium: announcement.utmMedium || '',
        utmCampaign: announcement.utmCampaign || ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSave = async () => {
    if (!formData.title || !formData.message) {
      toast.error('Title and message are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        priority: parseInt(formData.priority) || 0,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null
      };

      if (editingAnnouncement) {
        await api.put(`/admin/announcements/${editingAnnouncement.id}`, payload);
        toast.success('Announcement updated successfully');
      } else {
        await api.post('/admin/announcements', payload);
        toast.success('Announcement created successfully');
      }

      handleCloseModal();
      fetchAnnouncements();
    } catch (err) {
      console.error('Error saving announcement:', err);
      toast.error(err.message || 'Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.put(`/admin/announcements/${id}/toggle`);
      toast.success('Announcement status updated');
      fetchAnnouncements();
    } catch (err) {
      toast.error('Failed to toggle announcement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      await api.delete(`/admin/announcements/${id}`);
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (err) {
      toast.error('Failed to delete announcement');
    }
  };

  const getStatusBadge = (announcement) => {
    const status = announcement.computedStatus || 'active';
    const statusClasses = {
      active: 'ann-status-active',
      scheduled: 'ann-status-scheduled',
      expired: 'ann-status-expired',
      inactive: 'ann-status-inactive'
    };
    return <span className={`ann-status-badge ${statusClasses[status]}`}>{status}</span>;
  };

  const renderThemePreview = (theme) => {
    const config = THEME_CONFIG[theme];
    const Icon = config.icon;
    return (
      <div
        className="ann-theme-preview"
        style={{
          background: `linear-gradient(135deg, ${config.colors[0]} 0%, ${config.colors[1]} 100%)`,
          color: config.textColor
        }}
      >
        <Icon size={16} />
        <span>{config.label}</span>
      </div>
    );
  };

  return (
    <div className="ann-container">
      {/* Header */}
      <div className="ann-header">
        <div className="ann-header-left">
          <Megaphone size={28} className="ann-header-icon" />
          <div>
            <h1>Announcements</h1>
            <p>Manage merchant dashboard banners</p>
          </div>
        </div>
        <button className="ann-btn ann-btn-primary" onClick={() => handleOpenModal()}>
          <Plus size={18} />
          New Announcement
        </button>
      </div>

      {/* Filters */}
      <div className="ann-filters">
        <div className="ann-filter-tabs">
          {['all', 'active', 'scheduled', 'expired'].map(status => (
            <button
              key={status}
              className={`ann-filter-tab ${statusFilter === status ? 'active' : ''}`}
              onClick={() => setStatusFilter(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Announcements List */}
      {loading ? (
        <div className="ann-loading">Loading announcements...</div>
      ) : announcements.length === 0 ? (
        <div className="ann-empty">
          <Megaphone size={48} />
          <h3>No announcements</h3>
          <p>Create your first announcement to display on merchant dashboards</p>
          <button className="ann-btn ann-btn-primary" onClick={() => handleOpenModal()}>
            <Plus size={18} />
            Create Announcement
          </button>
        </div>
      ) : (
        <div className="ann-list">
          {announcements.map(announcement => (
            <div key={announcement.id} className="ann-card">
              <div className="ann-card-header">
                <div className="ann-card-title-row">
                  {renderThemePreview(announcement.theme)}
                  <h3>{announcement.title}</h3>
                  {getStatusBadge(announcement)}
                </div>
                <div className="ann-card-actions">
                  <button
                    className="ann-action-btn"
                    onClick={() => handleToggle(announcement.id)}
                    title={announcement.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {announcement.isActive ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <button
                    className="ann-action-btn"
                    onClick={() => handleOpenModal(announcement)}
                    title="Edit"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="ann-action-btn ann-action-delete"
                    onClick={() => handleDelete(announcement.id)}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <p className="ann-card-message">{announcement.message}</p>
              <div className="ann-card-meta">
                <span className="ann-meta-item">
                  <Target size={14} />
                  {targetAudiences.find(a => a.value === announcement.targetAudience)?.label || announcement.targetAudience}
                </span>
                {announcement.buttonUrl && (
                  <span className="ann-meta-item">
                    <Link size={14} />
                    {announcement.buttonText || 'Learn More'}
                  </span>
                )}
                {(announcement.startDate || announcement.endDate) && (
                  <span className="ann-meta-item">
                    <Calendar size={14} />
                    {announcement.startDate?.split('T')[0]} - {announcement.endDate?.split('T')[0] || 'No end'}
                  </span>
                )}
              </div>

              {/* Performance Metrics */}
              <div className="ann-performance">
                <div className="ann-perf-item">
                  <BarChart3 size={14} />
                  <span>{(announcement.impressions || 0).toLocaleString()} views</span>
                </div>
                <div className="ann-perf-item">
                  <MousePointer size={14} />
                  <span>{(announcement.clicks || 0).toLocaleString()} clicks</span>
                </div>
                <div className="ann-perf-item">
                  <XCircle size={14} />
                  <span>{(announcement.dismissals || 0).toLocaleString()} dismissed</span>
                </div>
                {announcement.impressions > 0 && (
                  <div className="ann-perf-item ann-perf-rate">
                    <TrendingUp size={14} />
                    <span>
                      {((announcement.clicks || 0) / announcement.impressions * 100).toFixed(1)}% CTR
                      {' · '}
                      {((announcement.dismissals || 0) / announcement.impressions * 100).toFixed(1)}% dismiss
                    </span>
                  </div>
                )}
              </div>

              {/* Live Preview */}
              <div className="ann-live-preview">
                <span className="ann-preview-label">Preview:</span>
                <div
                  className="ann-preview-banner"
                  style={{
                    background: `linear-gradient(135deg, ${THEME_CONFIG[announcement.theme].colors[0]} 0%, ${THEME_CONFIG[announcement.theme].colors[1]} 100%)`,
                    color: THEME_CONFIG[announcement.theme].textColor
                  }}
                >
                  {announcement.imageUrl && (
                    <img src={announcement.imageUrl} alt="" className="ann-preview-image" />
                  )}
                  <div className="ann-preview-text">
                    <strong>{announcement.title}</strong>
                    <span>{announcement.message}</span>
                  </div>
                  {announcement.buttonUrl && (
                    <span className="ann-preview-btn">{announcement.buttonText || 'Learn More'}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="ann-modal-overlay" onClick={handleCloseModal}>
          <div className="ann-modal" onClick={e => e.stopPropagation()}>
            <div className="ann-modal-header">
              <h2>{editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}</h2>
              <button className="ann-modal-close" onClick={handleCloseModal}>
                <X size={20} />
              </button>
            </div>

            <div className="ann-modal-body">
              {/* Title */}
              <div className="ann-form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Announcement title"
                />
              </div>

              {/* Message */}
              <div className="ann-form-group">
                <label>Message *</label>
                <textarea
                  value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Announcement message"
                  rows={3}
                />
              </div>

              {/* Theme Selection */}
              <div className="ann-form-group">
                <label>Theme</label>
                <div className="ann-theme-grid">
                  {Object.entries(THEME_CONFIG).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`ann-theme-option ${formData.theme === key ? 'selected' : ''}`}
                        onClick={() => setFormData({ ...formData, theme: key })}
                        style={{
                          background: `linear-gradient(135deg, ${config.colors[0]} 0%, ${config.colors[1]} 100%)`,
                          color: config.textColor
                        }}
                      >
                        <Icon size={18} />
                        <span>{config.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Image URL */}
              <div className="ann-form-group">
                <label>
                  <Image size={14} />
                  Image URL (optional)
                </label>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={e => setFormData({ ...formData, imageUrl: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              {/* Button */}
              <div className="ann-form-row">
                <div className="ann-form-group">
                  <label>Button Text</label>
                  <input
                    type="text"
                    value={formData.buttonText}
                    onChange={e => setFormData({ ...formData, buttonText: e.target.value })}
                    placeholder="Learn More"
                  />
                </div>
                <div className="ann-form-group">
                  <label>Button URL</label>
                  <input
                    type="url"
                    value={formData.buttonUrl}
                    onChange={e => setFormData({ ...formData, buttonUrl: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>

              {/* Target & Priority */}
              <div className="ann-form-row">
                <div className="ann-form-group">
                  <label>Target Audience</label>
                  <select
                    value={formData.targetAudience}
                    onChange={e => setFormData({ ...formData, targetAudience: e.target.value })}
                  >
                    {targetAudiences.map(a => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </select>
                </div>
                <div className="ann-form-group">
                  <label>Priority</label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={e => setFormData({ ...formData, priority: e.target.value })}
                    min="0"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Schedule */}
              <div className="ann-form-row">
                <div className="ann-form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                  />
                </div>
                <div className="ann-form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                  />
                </div>
              </div>

              {/* UTM Tracking */}
              <div className="ann-form-group">
                <label>UTM Tracking (Optional)</label>
                <p className="ann-form-hint" style={{ marginTop: 0, marginBottom: 8 }}>
                  These parameters will be appended to the button URL for tracking
                </p>
                <div className="ann-form-row" style={{ marginTop: 8 }}>
                  <input
                    type="text"
                    value={formData.utmSource}
                    onChange={e => setFormData({ ...formData, utmSource: e.target.value })}
                    placeholder="utm_source (e.g., dashboard)"
                  />
                  <input
                    type="text"
                    value={formData.utmMedium}
                    onChange={e => setFormData({ ...formData, utmMedium: e.target.value })}
                    placeholder="utm_medium (e.g., banner)"
                  />
                </div>
                <input
                  type="text"
                  value={formData.utmCampaign}
                  onChange={e => setFormData({ ...formData, utmCampaign: e.target.value })}
                  placeholder="utm_campaign (e.g., valentines_promo)"
                  style={{ marginTop: 8 }}
                />
              </div>

              {/* Active Toggle */}
              <div className="ann-form-group ann-toggle-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
                <p className="ann-form-hint">When active, this announcement will be shown to merchants</p>
              </div>

              {/* Live Preview */}
              <div className="ann-form-group">
                <label>Live Preview</label>
                <div
                  className="ann-preview-banner ann-preview-modal"
                  style={{
                    background: `linear-gradient(135deg, ${THEME_CONFIG[formData.theme].colors[0]} 0%, ${THEME_CONFIG[formData.theme].colors[1]} 100%)`,
                    color: THEME_CONFIG[formData.theme].textColor
                  }}
                >
                  {formData.imageUrl && (
                    <img src={formData.imageUrl} alt="" className="ann-preview-image" />
                  )}
                  <div className="ann-preview-text">
                    <strong>{formData.title || 'Announcement Title'}</strong>
                    <span>{formData.message || 'Your announcement message will appear here'}</span>
                  </div>
                  {formData.buttonUrl && (
                    <span className="ann-preview-btn">{formData.buttonText || 'Learn More'}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="ann-modal-footer">
              <button className="ann-btn ann-btn-secondary" onClick={handleCloseModal}>
                Cancel
              </button>
              <button
                className="ann-btn ann-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : editingAnnouncement ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementManagement;
